import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmailList } from "./EmailList";
import { ComposeEmailModal, type EmailRecipientSuggestion } from "./modals/ComposeEmailModal";
import { EmailDetailModal } from "./modals/EmailDetailModal";
import { ImportGmailThreadModal } from "./modals/ImportGmailThreadModal";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { useClaimEmails, useGmail } from "@/hooks/use-gmail";
import { useDocuments } from "@/hooks/use-documents";
import { useClaimPhotos } from "@/hooks/use-claim-photos";
import { useClaimContacts, useClaimOrganizations } from "@/hooks/use-claim-directory-links";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import { ClaimDetailed, ClaimEmailWithRecipients } from "@/api/generated/client/models";
import { AlertCircle, Download, Loader2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface EmailPanelProps {
    claimId: number;
    canWrite: boolean;
    claim?: Pick<ClaimDetailed, "insuredName" | "insuredEmail" | "insuredEmail2" | "assignees">;
}

// Tracks the last successful auto-sync time per claim. Kept at module scope
// (not a ref) because the center Email panel unmounts when the user switches to
// another center tab and remounts when they return — a ref would reset on every
// mount and re-trigger a Gmail sync on each tab switch.
const lastAutoSyncByClaim = new Map<number, number>();
const inFlightAutoSyncClaims = new Map<number, number>();
const AUTO_SYNC_STALENESS_MS = 5 * 60 * 1000;
const AUTO_SYNC_TRACKING_TTL_MS = 60 * 60 * 1000;
const IN_FLIGHT_SYNC_TTL_MS = 10 * 60 * 1000;
const MAX_TRACKED_CLAIMS = 200;
const RECIPIENT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Backend error codes that mean the Gmail token can no longer read mail and the
// user must reconnect (revoked token or missing/insufficient OAuth scope).
const GMAIL_AUTH_ERROR_CODES = new Set(['GMAIL_SCOPE_ERROR', 'GMAIL_RECONNECT_REQUIRED']);

// The OpenAPI generated client throws a ResponseError whose `.response` is a raw
// Response with an unparsed JSON body, so the backend error code isn't available
// synchronously. Read it here (best-effort) to decide whether a failed auto-sync
// was an actionable auth/scope problem worth surfacing to the user.
async function resolveGmailAuthErrorCode(error: unknown): Promise<string | undefined> {
    if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        (error as { response?: unknown }).response instanceof Response
    ) {
        try {
            const data = await (error as { response: Response }).response.clone().json();
            return data?.error?.code ?? data?.code;
        } catch {
            return undefined;
        }
    }
    return undefined;
}

function pruneAutoSyncTracking(now: number) {
    const staleCompletedBefore = now - AUTO_SYNC_TRACKING_TTL_MS;
    for (const [trackedClaimId, completedAt] of lastAutoSyncByClaim.entries()) {
        if (completedAt < staleCompletedBefore) {
            lastAutoSyncByClaim.delete(trackedClaimId);
        }
    }

    const staleInFlightBefore = now - IN_FLIGHT_SYNC_TTL_MS;
    for (const [trackedClaimId, startedAt] of inFlightAutoSyncClaims.entries()) {
        if (startedAt < staleInFlightBefore) {
            inFlightAutoSyncClaims.delete(trackedClaimId);
        }
    }

    if (lastAutoSyncByClaim.size > MAX_TRACKED_CLAIMS) {
        const oldestFirst = [...lastAutoSyncByClaim.entries()]
            .sort((a, b) => a[1] - b[1]);
        const overflowCount = lastAutoSyncByClaim.size - MAX_TRACKED_CLAIMS;
        for (let i = 0; i < overflowCount; i += 1) {
            const [oldestClaimId] = oldestFirst[i];
            lastAutoSyncByClaim.delete(oldestClaimId);
        }
    }

    if (inFlightAutoSyncClaims.size > MAX_TRACKED_CLAIMS) {
        const oldestFirst = [...inFlightAutoSyncClaims.entries()]
            .sort((a, b) => a[1] - b[1]);
        const overflowCount = inFlightAutoSyncClaims.size - MAX_TRACKED_CLAIMS;
        for (let i = 0; i < overflowCount; i += 1) {
            const [oldestClaimId] = oldestFirst[i];
            inFlightAutoSyncClaims.delete(oldestClaimId);
        }
    }
}

export function EmailPanel({ claimId, canWrite, claim }: EmailPanelProps) {
    const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<ClaimEmailWithRecipients | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isReplyFormOpen, setIsReplyFormOpen] = useState(false);
    const [openDetailInReplyMode, setOpenDetailInReplyMode] = useState(false);
    const [emailToDelete, setEmailToDelete] = useState<ClaimEmailWithRecipients | null>(null);
    // Set when an auto-sync fails with an actionable Gmail auth/scope error, so we
    // can prompt the user to reconnect instead of silently importing nothing.
    const [gmailAuthError, setGmailAuthError] = useState(false);

    // Signature lookup triggers an additional Gmail sendAs.list call server-side.
    // Only request signature fields while compose/reply UI is active.
    const shouldIncludeSignature =
        isComposeModalOpen || isReplyFormOpen;

    const { emails, total, isLoading, sendEmail, isSending, syncEmails, deleteEmailAsync, getEmailAttachmentBlob } = useClaimEmails(claimId);
    const { documents: claimDocuments, isLoading: areDocumentsLoading, getDocumentBlob } = useDocuments(claimId, isComposeModalOpen);
    const { photos: claimPhotos, isLoading: arePhotosLoading, getPhotoBlob } = useClaimPhotos(claimId, false, isComposeModalOpen);
    const { contacts: linkedContacts } = useClaimContacts(claimId, isComposeModalOpen);
    const { organizations: linkedOrganizations } = useClaimOrganizations(claimId, isComposeModalOpen);
    const { connected, account, composeSignatureText } = useGmail({ includeSignature: shouldIncludeSignature });
    const { toast } = useToast();
    const recipientSuggestions = useMemo<EmailRecipientSuggestion[]>(() => {
        const suggestions: EmailRecipientSuggestion[] = [];
        const seenEmails = new Set<string>();
        const addSuggestion = (email: string | null | undefined, label: string) => {
            const normalizedEmail = email?.trim();
            if (!normalizedEmail || !RECIPIENT_EMAIL_REGEX.test(normalizedEmail)) return;
            const key = normalizedEmail.toLowerCase();
            if (seenEmails.has(key)) return;
            seenEmails.add(key);
            suggestions.push({ email: normalizedEmail, label });
        };

        const insuredLabel = claim?.insuredName ? `Insured: ${claim.insuredName}` : "Insured email";
        addSuggestion(claim?.insuredEmail, insuredLabel);
        const secondaryInsuredLabel = claim?.insuredName
            ? `Secondary insured: ${claim.insuredName}`
            : "Secondary insured email";
        addSuggestion(claim?.insuredEmail2, secondaryInsuredLabel);
        for (const contact of linkedContacts) {
            const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
            const label = contact.role ? `${name || contact.email} (${contact.role})` : name || contact.email || "Claim contact";
            addSuggestion(contact.email, label);
        }
        for (const organization of linkedOrganizations) {
            const label = organization.role
                ? `${organization.organizationName || "Claim organization"} (${organization.role})`
                : organization.organizationName || "Claim organization";
            addSuggestion(organization.organizationEmail, label);
        }
        for (const assignee of claim?.assignees ?? []) {
            addSuggestion(assignee.email, assignee.name ? `Claim member: ${assignee.name}` : "Claim member");
        }
        return suggestions;
    }, [claim, linkedContacts, linkedOrganizations]);

    // Auto-sync replies when the panel mounts (with 5-minute staleness check),
    // so a user living in the center Email tab still sees new received emails
    // without opening the sidebar module.
    useEffect(() => {
        // Guard against state updates after unmount — the panel remounts on tab
        // switches, so syncEmails() may still be in-flight when this effect is
        // torn down (see module-scope tracking above).
        let cancelled = false;
        const now = Date.now();
        pruneAutoSyncTracking(now);

        if (!connected || inFlightAutoSyncClaims.has(claimId)) return;
        const last = lastAutoSyncByClaim.get(claimId) ?? 0;
        if (now - last < AUTO_SYNC_STALENESS_MS) return;
        inFlightAutoSyncClaims.set(claimId, now);
        syncEmails()
            // Stamp freshness at completion time on success only; stamping on
            // failure would wrongly suppress retries for the full window.
            .then(() => { lastAutoSyncByClaim.set(claimId, Date.now()); if (!cancelled) setGmailAuthError(false); })
            // Silently ignore transient failures, but surface an actionable
            // reconnect prompt when the token lost read access (revoked/scope).
            .catch(async (error) => {
                const code = await resolveGmailAuthErrorCode(error);
                if (!cancelled && code && GMAIL_AUTH_ERROR_CODES.has(code)) setGmailAuthError(true);
            })
            .finally(() => { inFlightAutoSyncClaims.delete(claimId); });

        return () => { cancelled = true; };
    }, [connected, claimId, syncEmails]);

    const handleSendEmail = async (params: {
        to: Array<{ email: string; name?: string }>;
        cc?: Array<{ email: string; name?: string }>;
        bcc?: Array<{ email: string; name?: string }>;
        subject: string;
        bodyText?: string;
        bodyHtml?: string;
        attachments?: Array<{ filename: string; mimeType: string; contentBase64: string }>;
    }) => {
        if (!canWrite) return;
        try {
            await sendEmail(params);
            setIsComposeModalOpen(false);
            toast({ title: "Success", description: "Email sent successfully" });
        } catch (error) {
            console.error("Failed to send email:", error);
            toast({ title: "Error", description: getErrorMessage(error), variant: "destructive" });
            throw error; // Re-throw so the modal knows the send failed and stays open
        }
    };

    const handleSelectEmail = (email: ClaimEmailWithRecipients) => {
        setSelectedEmail(email);
        setIsReplyFormOpen(false);
        setOpenDetailInReplyMode(false);
        setIsDetailModalOpen(true);
    };

    const handleReplyFromList = (email: ClaimEmailWithRecipients) => {
        setSelectedEmail(email);
        setIsReplyFormOpen(true);
        setOpenDetailInReplyMode(true);
        setIsDetailModalOpen(true);
    };

    const handleReplyEmail = async (params: {
        to: Array<{ email: string; name?: string }>;
        subject: string;
        bodyText: string;
        inReplyToGmailThreadId?: string;
    }) => {
        try {
            await sendEmail(params);
            toast({ title: "Reply Sent", description: "Your reply was sent successfully" });
        } catch (error) {
            console.error("Failed to send reply:", error);
            toast({ title: "Reply Failed", description: getErrorMessage(error), variant: "destructive" });
            throw error; // Re-throw so the reply form stays open
        }
    };

    const handleDownloadAttachment = async (attachmentId: number, filename: string) => {
        if (!selectedEmail) return;
        try {
            const blob = await getEmailAttachmentBlob(selectedEmail.id, attachmentId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
        } catch (error) {
            toast({ title: "Download Failed", description: getErrorMessage(error), variant: "destructive" });
        }
    };

    return (
        <ScrollArea className="h-full w-full">
            <div className="p-6">
                <div className="mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-semibold">Emails</h2>
                            {total > 0 && (
                                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                    {total}
                                </Badge>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Send, receive, and import claim emails</p>
                    </div>
                </div>

                {!connected && (
                    <Alert variant="default" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                            Connect your Gmail account in Settings to send, sync, and import emails from claims.
                        </AlertDescription>
                    </Alert>
                )}

                {connected && gmailAuthError && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                            Gmail sync failed. Reconnect your Gmail account in Settings.
                        </AlertDescription>
                    </Alert>
                )}

                {!canWrite && (
                    <Alert variant="default" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                            Read-only access: composing, importing, replying, and deleting emails are blocked.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Button size="sm" onClick={() => setIsComposeModalOpen(true)} disabled={!connected || !canWrite}>
                        <Plus className="h-4 w-4 mr-2" /> Compose Email
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsImportModalOpen(true)} disabled={!connected || !canWrite}>
                        <Download className="h-4 w-4 mr-2" /> Import from Gmail
                    </Button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <EmailList
                        emails={emails}
                        total={total}
                        onSelectEmail={handleSelectEmail}
                        onReplyEmail={connected && canWrite ? handleReplyFromList : undefined}
                        onDeleteEmail={canWrite ? (email) => setEmailToDelete(email) : undefined}
                        scrollable={false}
                    />
                )}
            </div>

            {/* Modals */}
            <ComposeEmailModal
                open={isComposeModalOpen}
                onOpenChange={setIsComposeModalOpen}
                onSend={handleSendEmail}
                isSending={isSending}
                fromEmail={account?.gmailEmail}
                defaultBody={composeSignatureText ?? undefined}
                claimDocuments={claimDocuments}
                documentsLoading={areDocumentsLoading}
                getDocumentBlob={getDocumentBlob}
                claimPhotos={claimPhotos}
                photosLoading={arePhotosLoading}
                getPhotoBlob={getPhotoBlob}
                recipientSuggestions={recipientSuggestions}
            />

            <EmailDetailModal
                email={selectedEmail}
                open={isDetailModalOpen}
                onOpenChange={(open) => {
                    setIsDetailModalOpen(open);
                    if (!open) {
                        setIsReplyFormOpen(false);
                        setOpenDetailInReplyMode(false);
                    }
                }}
                onReply={connected && canWrite ? handleReplyEmail : undefined}
                isReplying={isSending}
                startWithReplyForm={openDetailInReplyMode}
                defaultReplyBody={composeSignatureText ?? undefined}
                onReplyFormOpenChange={setIsReplyFormOpen}
                onDownloadAttachment={handleDownloadAttachment}
            />

            <ImportGmailThreadModal
                open={isImportModalOpen}
                onOpenChange={setIsImportModalOpen}
                claimId={claimId}
            />

            <ConfirmDeleteDialog
                open={emailToDelete !== null}
                onOpenChange={(open) => {
                    if (!open) setEmailToDelete(null);
                }}
                onConfirm={async () => {
                    if (!emailToDelete) return;
                    if (!canWrite) return;
                    try {
                        await deleteEmailAsync(emailToDelete.id);
                        toast({ title: "Email removed", description: "The email was removed from this claim." });
                    } catch (error) {
                        toast({ title: "Error", description: getErrorMessage(error), variant: "destructive" });
                    }
                }}
                title="Remove email"
                description={`Remove "${emailToDelete?.subject || "(No Subject)"}" from this claim's email list? It won't be re-imported by a future sync.`}
            />
        </ScrollArea>
    );
}
